import {
    ApplicationPublicRole,
    OneVerifyApplicationRole,
} from "@common-shared/config/rolesPermissions.config.ts"
import {
    IOneVerifyUpdateRoleData,
    IOneVerifyUpdateRoleDataUnmapped,
    IOneVerifyUserInviteData,
    IOneVerifyUserInviteDataUnmapped,
} from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"
import {
    IOneVerifyOrganizationUser,
    IOneVerifyOrganizationUserInvite,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { API_ROUTES } from "../config/routes.config"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper.tsx"

export class OrganizationUsersService {
    /**
     * Get list of portal users within practice
     */
    static async getUsersList(
        practice: IPractice | number,
    ): Promise<IOneVerifyOrganizationUser[] | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_GET_LIST.replace(
                ":practiceId",
                (typeof practice === "number"
                    ? practice
                    : practice.id
                ).toString(),
            ),
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Delete user from practice
     */
    static async deleteUser(
        practice: IPractice,
        user: IOneVerifyOrganizationUser,
    ): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_DELETE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":userId", user.id.toString()),
            undefined,
            "DELETE",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Reset user's MFA setup
     */
    static async resetUserMFASetup(
        practice: IPractice,
        user: IOneVerifyOrganizationUser,
    ): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_RESET_MFA.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":userId", user.id.toString()),
            undefined,
            "DELETE",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Change user role in practice
     */
    static async changeUserRole(
        practice: IPractice,
        user: IOneVerifyOrganizationUser,
        data: IOneVerifyUpdateRoleDataUnmapped,
    ): Promise<IOneVerifyOrganizationUser | undefined> {
        const requestData = OrganizationUsersService.mapOneVerifyUserRole<
            IOneVerifyUpdateRoleDataUnmapped,
            IOneVerifyUpdateRoleData
        >(data)

        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_CHANGE_ROLE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":userId", user.id.toString()),
            requestData,
            "PUT",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Invite user to organization
     */
    static async inviteUser(
        practice: IPractice,
        data: IOneVerifyUserInviteDataUnmapped,
    ): Promise<
        | {
              isNew: boolean
              invite: IOneVerifyOrganizationUserInvite
          }
        | undefined
    > {
        const requestData = OrganizationUsersService.mapOneVerifyUserRole<
            IOneVerifyUserInviteDataUnmapped,
            IOneVerifyUserInviteData
        >(data)

        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_INVITE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
            "POST",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Get list of users invites within practice
     */
    static async getInvitesList(
        practice: IPractice,
    ): Promise<IOneVerifyOrganizationUserInvite[] | undefined> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_GET_INVITES_LIST.replace(
                ":practiceId",
                practice.id.toString(),
            ),
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Delete user invite from practice
     */
    static async deleteUserInvite(
        practice: IPractice,
        invite: IOneVerifyOrganizationUserInvite,
    ): Promise<any> {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_DELETE_INVITE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":inviteId", invite.id.toString()),
            undefined,
            "DELETE",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Change user invite role in practice
     */
    static async changeUserInviteRole(
        practice: IPractice,
        invite: IOneVerifyOrganizationUserInvite,
        data: IOneVerifyUpdateRoleDataUnmapped,
    ): Promise<IOneVerifyOrganizationUserInvite | undefined> {
        const requestData = OrganizationUsersService.mapOneVerifyUserRole<
            IOneVerifyUpdateRoleDataUnmapped,
            IOneVerifyUpdateRoleData
        >(data)

        const result = await HttpHelper.sendRequest(
            API_ROUTES.ORGANIZATION_USERS_CHANGE_INVITE_ROLE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":inviteId", invite.id.toString()),
            requestData,
            "PUT",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Map user object to make sure role is properly typed to what backend expects
     */
    private static mapOneVerifyUserRole<
        Type extends { role: OneVerifyApplicationRole },
        ReturnType extends { role: ApplicationPublicRole },
    >(obj: Type): ReturnType {
        return {
            ...obj,

            role: obj.role as unknown as ApplicationPublicRole,
        }
    }
}
